/**
 * L'encadré qui dit où est le feu et si la fumée est là.
 *
 * Phrases courtes. C'est lu en vitesse, parfois par quelqu'un d'inquiet.
 */
import { NEARBY_KM, SENSOR_TRUST_KM } from '../../core/constants';
import { directionTo, durationWords, formatClock } from '../../core/format';
import { isRealFire, isWeakDetection } from '../../core/zones';
import type { DisplayedVerdict, NearbyFires, ZoneWithPlume } from '../../state/selectors';
import { selectLocalAir } from '../../state/selectors';
import type { AppState } from '../../state/store';
import { html, type Html } from '../html';

/** Ce que mesurent les capteurs proches, ou l'aveu qu'il n'y en a pas. */
function groundTruth(state: AppState, now: number): Html {
  const air = selectLocalAir(state, now);

  if (air.pm25 === null) {
    return html`
      <strong>
        Aucun capteur à moins de ${SENSOR_TRUST_KM} km. On ne sait pas si la fumée
        descend jusqu'au sol.
      </strong>
    `;
  }

  return air.pm25 < 12
    ? html`
        <strong>
          Les capteurs près de toi mesurent ${air.pm25.toFixed(1)} µg/m³. Elle ne descend
          pas jusqu'ici.
        </strong>
      `
    : html`
        <strong>
          Les capteurs mesurent déjà ${air.pm25.toFixed(1)} µg/m³. Elle est là.
        </strong>
      `;
}

/**
 * Quand la fumée arrive, ou pourquoi on ne peut pas le dire.
 *
 * Chaque cas a sa phrase. L'ancienne version retombait sur "sa fumée est déjà
 * sur toi" dès qu'aucune heure n'était calculable, ce qui était faux quand la
 * vraie raison était un vent trop lent.
 */
function plumeTiming(entry: ZoneWithPlume, now: number): Html {
  const plume = entry.plume;
  if (!plume) return html``;

  if (plume.arrival !== null) {
    return html`
      Le vent a tourné à ${formatClock(plume.steadySince, now)}. Elle devrait arriver vers
      <strong>${formatClock(plume.arrival, now)}</strong>.
    `;
  }

  switch (plume.arrivalUnavailable) {
    case 'established':
      return html`
        Le vent souffle comme ça depuis ${durationWords(plume.hoursSinceShift)}. Sa fumée
        est déjà là.
      `;
    case 'too-slow':
      return html`Vent trop faible pour dire quand elle arriverait.`;
    case 'too-far-ahead':
      return html`Plus de deux heures de trajet, le vent aura tourné avant.`;
    default:
      return html``;
  }
}

export function plumeNotice(
  state: AppState,
  fires: NearbyFires,
  displayed: DisplayedVerdict,
  now: number,
): Html {
  /*
   * Le foyer décrit ici est celui qui a fondé le verdict, dès qu'un plancher de
   * prudence s'applique. Sinon l'encadré pouvait détailler un feu à 127 km
   * pendant que le titre alertait sur un foyer à 10 km.
   */
  const primary =
    (displayed.precautionary ? displayed.trigger : null) ??
    fires.enRoute ??
    fires.couldReach[0] ??
    fires.headline;

  const count = fires.couldReach.length;

  if (primary?.plume?.couldReach) {
    const plume = primary.plume;
    return html`
      <p class="notice" data-kind="warn">
        <strong>
          ${count > 1
            ? `${count} feux peuvent t'envoyer leur fumée.`
            : "Un feu peut t'envoyer sa fumée."}
        </strong>
        ${primary.zone.place?.label ?? 'Le foyer'} est à
        ${Math.round(plume.alongKm ?? plume.distanceKm)} km ${directionTo(plume.bearing)}.
        Vent de ${Math.round(plume.windSpeed ?? 0)} km/h à ${plume.level.label}${plume
          .travelHours !== null
          ? `, ${durationWords(plume.travelHours)} de trajet`
          : ''}.
        ${plumeTiming(primary, now)} ${groundTruth(state, now)}
      </p>
    `;
  }

  if (primary?.plume?.weakWind) {
    return html`
      <p class="notice" data-kind="${displayed.precautionary ? 'warn' : 'note'}">
        <strong>Vent trop faible pour savoir où part la fumée.</strong>
        Feu le plus proche à ${Math.round(primary.distanceKm)} km
        ${directionTo(primary.plume.bearing)}. ${groundTruth(state, now)}
      </p>
    `;
  }

  if (primary?.plume) {
    return displayed.precautionary
      ? html`
          <p class="notice" data-kind="warn">
            <strong>Le vent ne porte pas sa fumée vers toi.</strong>
            Mais ce feu est à ${Math.round(primary.distanceKm)} km
            ${directionTo(primary.plume.bearing)} : une rotation du vent change tout.
            ${groundTruth(state, now)}
          </p>
        `
      : html`
          <p class="notice" data-kind="calm">
            <strong>Aucun feu dans l'axe du vent.</strong> Le plus proche est à
            ${Math.round(primary.distanceKm)} km ${directionTo(primary.plume.bearing)}.
          </p>
        `;
  }

  if (state.scanning) {
    const progress = state.scanProgress;
    return html`
      <p class="notice" data-kind="note" aria-live="polite">
        Recherche des feux en France.
        ${progress ? ` Image ${progress.done} sur ${progress.total}.` : ''}
      </p>
    `;
  }

  // Ne jamais présenter une panne comme une bonne nouvelle.
  if (state.zones.length === 0 && state.scanReport.failed > 0 && state.scanReport.ok === 0) {
    return html`
      <p class="notice" data-kind="note">
        <strong>Images satellite inaccessibles.</strong> Ce n'est pas une absence de feu.
        La qualité de l'air ci-dessous reste à jour.
      </p>
    `;
  }

  const elsewhere = state.zones.filter(
    (zone) => isRealFire(zone) && !isWeakDetection(zone),
  ).length;

  return html`
    <p class="notice" data-kind="calm">
      Aucun feu à moins de ${NEARBY_KM} km.
      ${elsewhere > 0 ? ` ${elsewhere} ailleurs en France.` : ''}
    </p>
  `;
}
